/*
 * Shared profile summary card — CMT-2026-09-03-026 (Duncan ruling: the
 * existing Profile-complete card IS the design — no new design).
 *
 * ONE component renders the summary rows in BOTH homes:
 *  - the wizard receipt (/get-ready/profile/complete — read-only, no pencils),
 *  - the account edit hub (/profile/edit — every row carries a per-row EDIT
 *    PENCIL that deep-links into the wizard step owning the field).
 *
 * The container + row geometry is the receipt card's measured markup moved
 * here verbatim (surface bg, hairline-08 border, radius 14, 12.5px muted key /
 * 13px 700 value); testids (`wiz-summary-row`, data-k) are preserved so every
 * existing receipt assertion keeps matching. The pencil is ADDITIVE — absent
 * `onEdit`, the render is byte-equivalent to the old receipt card.
 */
import type { CSSProperties } from 'react'
import type { SummaryRow } from './wizardModel'
import { IconPencil } from '../../lib/icons'

const cardStyle: CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--hairline-08)',
  borderRadius: 14, padding: '4px 15px',
}
const rowStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0',
  borderBottom: '1px solid var(--hairline-06)',
}
// Teal outline action voice (the Profile account card's Edit affordance
// family) — an ACTION colour, never the reserved status treatment.
const pencilStyle: CSSProperties = {
  flex: 'none', display: 'grid', placeItems: 'center', width: 38, height: 38,
  border: '1px solid rgba(63,224,200,.35)', borderRadius: 9, background: 'transparent',
  color: 'var(--accent-bright)', cursor: 'pointer', padding: 0,
}

export function SummaryCard({ rows, onEdit, editable, editLabel, style }: {
  rows: SummaryRow[]
  /** Present ⇒ every (editable) row renders the edit pencil deep-link. */
  onEdit?: (row: SummaryRow) => void
  /** Per-row pencil gate (default: every row). Used to HONESTLY drop the
   *  pencil on identity rows under the L2 edit lock — no dead affordance
   *  pointing at a read-only step. */
  editable?: (row: SummaryRow) => boolean
  /** Accessible label for a row's pencil (e.g. `Edit Date of birth`). */
  editLabel?: (row: SummaryRow) => string
  style?: CSSProperties
}) {
  return (
    <div data-testid="wiz-summary-card" style={{ ...cardStyle, ...style }}>
      {rows.map((r) => (
        <div key={r.id} data-testid="wiz-summary-row" data-k={r.k} style={rowStyle}>
          <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-muted)' }}>{r.k}</span>
          <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'end' }}>{r.v}</span>
          {onEdit && (editable?.(r) ?? true) && (
            <button
              data-testid="summary-row-edit"
              data-row={r.id}
              aria-label={editLabel?.(r) ?? r.k}
              onClick={() => onEdit(r)}
              style={pencilStyle}
            >
              <IconPencil size={14} />
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
